import { useState } from 'react';
import {
  Card,
  Empty,
  Input,
  Select,
  Skeleton,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  PERIOD_OPTIONS,
  PLATFORM_LABELS,
  PLATFORM_OPTIONS,
  useAudienceGrowth,
  useAudienceOverview,
  useAudienceUsers,
  type AudienceUser,
  type AudiencePeriod,
  type AudiencePlatform,
  type PlatformCounters,
} from '@entities/audience';

const { Title, Text } = Typography;

interface Props {
  assistantId: string;
}

const PAGE_SIZE = 50;

/**
 * Вкладка «Аудитория» в карточке ассистента (N-2).
 * Счётчики подписчиков РАЗДЕЛЬНО по платформам (Telegram/MAX/Виджет),
 * график роста подписчиков по дням + таблица подписчиков (поиск/фильтры/пагинация).
 * Дизайн «Графит»: спокойные тона, суммы Title level={3} weight 500.
 *
 * Подписчик = диалог (/start уже регистрирует). Раздельно по каналам — т.к.
 * рассылка идёт по каждому каналу отдельно (серия N).
 */
export function AudienceTab({ assistantId }: Props) {
  const [platform, setPlatform] = useState<AudiencePlatform>('all');
  const [period, setPeriod] = useState<AudiencePeriod>('month');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const { data: overview, isLoading: overviewLoading } =
    useAudienceOverview(assistantId);
  const { data: growth, isLoading: growthLoading } = useAudienceGrowth(
    assistantId,
    platform,
    period,
  );
  const { data: usersData, isLoading: usersLoading } = useAudienceUsers(
    assistantId,
    platform,
    period,
    search,
    page * PAGE_SIZE,
    PAGE_SIZE,
  );

  const users = usersData?.items ?? [];
  const byPlatform = overview?.by_platform ?? [];

  const columns: ColumnsType<AudienceUser> = [
    {
      title: 'Платформа',
      dataIndex: 'platform',
      key: 'platform',
      width: 120,
      render: (p: string) => (
        <Tag bordered={false} style={{ fontSize: 12 }}>
          {PLATFORM_LABELS[p] ?? p}
        </Tag>
      ),
    },
    {
      title: 'Пользователь',
      dataIndex: 'display_name',
      key: 'display_name',
      width: 180,
      render: (name: string | null, row) => (
        <Text style={{ fontSize: 13 }}>{name || row.external_id}</Text>
      ),
    },
    {
      title: 'Подписался',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (v: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateTime(v)}
        </Text>
      ),
    },
    {
      title: 'Сообщений',
      dataIndex: 'messages_count',
      key: 'messages_count',
      width: 110,
      align: 'center',
      render: (n: number) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {n}
        </Text>
      ),
    },
    {
      title: 'Последняя активность',
      dataIndex: 'last_message_at',
      key: 'last_message_at',
      width: 170,
      render: (v: string | null, row) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {v ? formatDateTime(v) : formatDateTime(row.created_at)}
        </Text>
      ),
    },
  ];

  return (
    <div>
      {/* Итого подписчиков */}
      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        }}
      >
        <CounterCard
          label="Всего подписчиков"
          value={overview?.total}
          loading={overviewLoading}
        />
        {overviewLoading ? (
          <>
            <PlatformCardSkeleton />
            <PlatformCardSkeleton />
          </>
        ) : (
          byPlatform.map((p) => <PlatformCard key={p.platform} data={p} />)
        )}
      </div>

      {/* График роста */}
      <Card style={{ marginTop: 16 }} styles={{ body: { padding: 20 } }}>
        <Text
          type="secondary"
          style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
        >
          Новые подписчики по дням
        </Text>
        {growthLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : growth && growth.by_day.length > 0 ? (
          <GrowthChart data={growth.by_day} />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">Нет данных за период</Text>}
          />
        )}
      </Card>

      {/* Фильтры */}
      <div style={{ display: 'flex', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
        <Select
          value={platform}
          onChange={(v) => {
            setPlatform(v);
            setPage(0);
          }}
          options={PLATFORM_OPTIONS}
          style={{ minWidth: 160 }}
        />
        <Select
          value={period}
          onChange={(v) => {
            setPeriod(v);
            setPage(0);
          }}
          options={PERIOD_OPTIONS}
          style={{ minWidth: 140 }}
        />
        <Input.Search
          allowClear
          placeholder="Поиск по имени или ID"
          onSearch={(v) => {
            setSearch(v.trim());
            setPage(0);
          }}
          style={{ maxWidth: 260 }}
        />
      </div>

      {/* Таблица подписчиков */}
      <div style={{ marginTop: 16 }}>
        {usersLoading ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : users.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">
                Подписчиков пока нет — они появятся, когда пользователи запустят
                бота в Telegram или MAX.
              </Text>
            }
          />
        ) : (
          <Table
            columns={columns}
            dataSource={users}
            rowKey="dialog_id"
            size="middle"
            pagination={{
              current: page + 1,
              pageSize: PAGE_SIZE,
              total: usersData?.total ?? 0,
              showSizeChanger: false,
              onChange: (p) => setPage(p - 1),
            }}
          />
        )}
      </div>
    </div>
  );
}

/** Простой счётчик (итог). */
function CounterCard({
  label,
  value,
  loading,
}: {
  label: string;
  value?: number;
  loading: boolean;
}) {
  return (
    <Card styles={{ body: { padding: 20 } }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      {loading ? (
        <Skeleton active paragraph={false} title={{ width: 60 }} />
      ) : (
        <Title
          level={3}
          style={{ margin: '4px 0 0', fontWeight: 500, color: '#1F2937' }}
        >
          {value ?? 0}
        </Title>
      )}
    </Card>
  );
}

/** Карточка одной платформы: total крупно + MAU/WAU/DAU мелко. */
function PlatformCard({ data }: { data: PlatformCounters }) {
  return (
    <Card styles={{ body: { padding: 20 } }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {PLATFORM_LABELS[data.platform] ?? data.platform}
      </Text>
      <Title
        level={3}
        style={{ margin: '4px 0 8px', fontWeight: 500, color: '#1F2937' }}
      >
        {data.total}
      </Title>
      <div style={{ display: 'flex', gap: 16 }}>
        <MiniStat label="MAU" value={data.mau} />
        <MiniStat label="WAU" value={data.wau} />
        <MiniStat label="DAU" value={data.dau} />
      </div>
    </Card>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {label}
      </Text>
      <div style={{ fontSize: 14, fontWeight: 500, color: '#1F2937' }}>
        {value}
      </div>
    </div>
  );
}

function PlatformCardSkeleton() {
  return (
    <Card styles={{ body: { padding: 20 } }}>
      <Skeleton active paragraph={{ rows: 2 }} title={{ width: 80 }} />
    </Card>
  );
}

/**
 * График роста: складываем всех подписчиков за день (по всем платформам под
 * текущим фильтром) в одну линию. Бэк отдаёт разбивку по платформам —
 * агрегируем по дате для наглядной линии прироста.
 */
function GrowthChart({
  data,
}: {
  data: { date: string; platform: string; count: number }[];
}) {
  const byDate = new Map<string, number>();
  for (const d of data) {
    byDate.set(d.date, (byDate.get(d.date) ?? 0) + d.count);
  }
  const chartData = Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date: formatDay(date), count }));

  return (
    <div style={{ width: '100%', height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="rchAudienceFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1F2937" stopOpacity={0.16} />
              <stop offset="100%" stopColor="#1F2937" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
          />
          <YAxis
            width={36}
            allowDecimals={false}
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            formatter={(v: number) => [`${v}`, 'Новых']}
            contentStyle={{
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid #E5E7EB',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}
            labelStyle={{ color: '#6B7280' }}
            cursor={{ stroke: '#E5E7EB' }}
          />
          <Area
            type="monotone"
            dataKey="count"
            stroke="#1F2937"
            strokeWidth={2}
            fill="url(#rchAudienceFill)"
            dot={{ r: 2.5, fill: '#1F2937', strokeWidth: 0 }}
            activeDot={{ r: 4, fill: '#1F2937', strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function formatDay(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
